import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Volume2, ArrowRight, Loader2, RotateCcw, Check } from "lucide-react";
import client from "@/lib/api";
import { useTts } from "@/hooks/useTts";
import { useLang } from "@/context/LangContext";

export default function Review() {
  const nav = useNavigate();
  const { play } = useTts();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [i, setI] = useState(0);
  const [sel, setSel] = useState(null);
  const [checked, setChecked] = useState(false);
  const [correct, setCorrect] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => { client.get("/learn/review").then((r) => setData(r.data)).catch(() => setData({ items: [] })); }, []);

  if (!data) return <div className="min-h-screen bg-[#F6F9FD] flex items-center justify-center"><Loader2 className="animate-spin text-[#2563EB]" /></div>;

  if (data.items.length === 0) return (
    <div className="min-h-screen bg-[#F6F9FD] flex flex-col items-center justify-center px-6 text-center">
      <RotateCcw className="text-slate-300" size={48} />
      <p className="font-bold text-[#0F172A] mt-4">{data.message || t("review_nothing")}</p>
      <button onClick={() => nav("/home")} className="mt-6 bg-[#2563EB] text-white font-bold px-6 py-3 rounded-2xl">{t("common_back_home")}</button>
    </div>
  );

  if (done) return (
    <div className="min-h-screen bg-[#F6F9FD] flex flex-col items-center justify-center px-6 text-center">
      <div className="w-20 h-20 rounded-full bg-[#2563EB]/10 flex items-center justify-center"><Check className="text-[#2563EB]" size={40} /></div>
      <h1 className="font-heading text-2xl font-extrabold text-[#0F172A] mt-5">{t("review_complete")}</h1>
      <p className="text-slate-500 mt-1">{t("review_score", { c: correct, t: data.items.length })}</p>
      <button onClick={() => nav("/home")} data-testid="review-done" className="mt-7 w-full max-w-[320px] bg-[#2563EB] text-white font-bold py-4 rounded-2xl">{t("common_done")}</button>
    </div>
  );

  const it = data.items[i];
  const ok = sel === it.answer;
  return (
    <div className="min-h-screen bg-[#F6F9FD] flex flex-col px-5">
      <div className="flex items-center gap-3 pt-6 pb-3">
        <button onClick={() => nav("/home")}><X className="text-slate-400" /></button>
        <div className="flex-1 h-2.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-[#2563EB]" style={{ width: `${(i / data.items.length) * 100}%` }} /></div>
        <span className="text-xs font-bold text-slate-400">{i + 1}/{data.items.length}</span>
      </div>
      <div className="flex-1 flex flex-col justify-center">
        <div className="flex items-center gap-2">
          <h2 className="font-heading text-xl font-extrabold text-[#0F172A]">{it.prompt}</h2>
          {it.audio_text && <button onClick={() => play(it.audio_text)} className="w-9 h-9 rounded-full bg-[#2563EB]/10 text-[#2563EB] flex items-center justify-center"><Volume2 size={17} /></button>}
        </div>
        <div className="mt-5 space-y-3">
          {it.options.map((o, idx) => {
            let cls = "border-slate-200 bg-white text-[#0F172A]";
            if (checked && idx === it.answer) cls = "border-emerald-400 bg-emerald-50 text-emerald-800";
            else if (checked && idx === sel) cls = "border-rose-400 bg-rose-50 text-rose-700";
            else if (sel === idx) cls = "border-[#2563EB] bg-[#2563EB]/5";
            return <button key={idx} disabled={checked} onClick={() => setSel(idx)} data-testid={`review-opt-${idx}`} className={`w-full text-left rounded-2xl px-4 py-3.5 border font-medium ${cls}`}>{o}</button>;
          })}
        </div>
      </div>
      <div className="pb-8">
        {!checked ? (
          <button onClick={() => { const good = sel === it.answer; setChecked(true); if (good) setCorrect((c) => c + 1); if (it.word) client.post("/learn/word-result", { language: data.language, word: it.word, correct: good }).catch(() => {}); }} disabled={sel === null} data-testid="review-check" className="w-full bg-[#2563EB] text-white font-bold py-4 rounded-2xl disabled:opacity-50">{t("common_check")}</button>
        ) : (
          <button onClick={() => { if (i + 1 >= data.items.length) setDone(true); else { setI(i + 1); setSel(null); setChecked(false); } }} data-testid="review-next" className="w-full bg-[#2563EB] text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-2">{t("common_continue")} <ArrowRight size={18} /></button>
        )}
      </div>
    </div>
  );
}
